<template>
  <ion-page>
    <PageHeaderWithMenu title="Каталог музыки" :scrolled="headerScrolled" />
    <ion-content :class="[isMobile ? 'main-background' : 'liga-web-background']" :fullscreen="true" @ionScroll="onContentScroll">
      <ion-card class="admin-card">
        <ion-card-content>
          <ion-button expand="block" @click="openCreateModal()">
            <ion-icon slot="start" :icon="add" />
            Новая папка
          </ion-button>
        </ion-card-content>
      </ion-card>

      <ion-card v-for="f in folders" :key="f.id" class="admin-card">
        <ion-card-header>
          <ion-card-title>{{ f.title }}</ion-card-title>
          <p class="slug-line">/{{ f.slug }}/ · {{ f.accessMode === 'RESTRICTED' ? 'ограничено' : 'всем' }}</p>
        </ion-card-header>
        <ion-card-content>
          <ion-button size="small" @click="selectFolder(f)">Треки и загрузка</ion-button>
          <ion-button size="small" fill="outline" @click="openEditModal(f)">Доступ</ion-button>
          <ion-button size="small" color="danger" fill="clear" @click="confirmDelete(f)">Удалить</ion-button>
        </ion-card-content>
      </ion-card>

      <ion-modal :is-open="showFolderModal" @didDismiss="closeFolderModal" class="admin-releases-modal">
        <ion-header>
          <ion-toolbar>
            <ion-title>{{ folderModalTitle }}</ion-title>
            <ion-buttons slot="end">
              <ion-button fill="clear" @click="closeFolderModal()"><ion-icon :icon="closeOutline" /></ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content class="ion-padding">
          <ion-card>
            <ion-card-header>
              <ion-card-title>{{ folderModalTitle }}</ion-card-title>
            </ion-card-header>
            <ion-card-content>
              <ion-item>
                <ion-input v-model="editTitle" label="Название" label-placement="stacked" />
              </ion-item>
              <ion-item>
                <ion-label>Доступ</ion-label>
                <ion-select v-model="editAccess" interface="popover">
                  <ion-select-option value="PUBLIC">Все пользователи</ion-select-option>
                  <ion-select-option value="RESTRICTED">Только выбранные</ion-select-option>
                </ion-select>
              </ion-item>
              <template v-if="editAccess === 'RESTRICTED'">
                <ion-searchbar v-model="userSearch" placeholder="Поиск по логину или имени" />
                <div class="upload-modal-file-list">
                  <ion-list class="transparent-list">
                    <ion-item v-for="u in filteredUsers" :key="u.id" button @click="toggleUserQuick(u.id)">
                      <ion-label>
                        <h3>{{ u.login }}</h3>
                        <p>{{ u.firstName }} {{ u.lastName }}</p>
                      </ion-label>
                      <ion-note slot="end">{{ selectedUserIds.has(u.id) ? '✓' : '' }}</ion-note>
                    </ion-item>
                  </ion-list>
                </div>
              </template>
              <ion-button expand="block" @click="saveFolderModal" :disabled="saving">
                {{ saving ? 'Сохранение…' : 'Сохранить' }}
              </ion-button>
            </ion-card-content>
          </ion-card>
        </ion-content>
      </ion-modal>

      <ion-modal :is-open="showTracksModal" @didDismiss="closeTracksModal" class="admin-releases-modal">
        <ion-header>
          <ion-toolbar>
            <ion-title>{{ selectedFolder?.title }}</ion-title>
            <ion-buttons slot="end">
              <ion-button @click="closeTracksModal()"><ion-icon :icon="closeOutline" /></ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>
        <ion-content class="ion-padding">
          <ion-card v-if="uploadQueue.length" class="admin-card">
            <ion-card-header>
              <ion-card-title>Имена на сервере</ion-card-title>
              <p class="slug-line">Можно менять только имя без расширения; суффикс файла совпадает с исходным.</p>
            </ion-card-header>
            <ion-card-content>
              <ion-item v-for="(q, i) in uploadQueue" :key="'q' + i">
                <ion-input
                  v-model="q.targetName"
                  label="Сохранить как"
                  label-placement="stacked"
                  @ionInput="onUploadTargetNameInput(q, $event)"
                />
              </ion-item>
              <ion-button expand="block" @click="flushUpload()" :disabled="uploadFlushing">
                {{ uploadFlushing ? 'Загрузка…' : 'Загрузить' }}
              </ion-button>
              <ion-button expand="block" fill="clear" :disabled="uploadFlushing" @click="uploadQueue = []">Отмена</ion-button>
            </ion-card-content>
          </ion-card>
          <div v-if="uploadProgressList.length > 0" class="upload-progress-list ion-padding-horizontal">
            <p class="upload-progress-heading">Загрузка файлов</p>
            <div v-for="(p, idx) in uploadProgressList" :key="'lib-up-' + idx" class="upload-progress-item">
              <p class="upload-progress-name">{{ p.fileName }}</p>
              <ion-progress-bar :value="p.percent / 100" type="determinate"></ion-progress-bar>
              <p class="upload-progress-status">{{ p.status }}</p>
            </div>
          </div>
          <ion-card class="admin-card">
            <ion-card-content>
              <ion-button expand="block" @click="pickAudio()">
                <ion-icon slot="start" :icon="musicalNotesOutline" />
                Добавить аудио
              </ion-button>
              <input ref="fileInput" type="file" multiple accept="audio/*" style="display: none" @change="onFilesPicked" />
            </ion-card-content>
          </ion-card>
          <div v-if="tracks.length" class="upload-modal-file-list">
            <ion-list class="transparent-list">
              <ion-item v-for="t in tracks" :key="t.id">
                <ion-label>
                  <h3>{{ t.name }}</h3>
                  <p>{{ t.id }}</p>
                </ion-label>
                <ion-button fill="clear" @click="promptRename(t)"><ion-icon :icon="createOutline" /></ion-button>
                <ion-button fill="clear" color="danger" @click="confirmDeleteTrack(t)"><ion-icon :icon="trashOutline" /></ion-button>
              </ion-item>
            </ion-list>
          </div>
          <div v-else class="upload-modal-file-list">
            <p class="empty-hint ion-padding-horizontal">Нет файлов.</p>
          </div>
        </ion-content>
      </ion-modal>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import { GlobalContext } from '@/model/globalcontext.model';
import { GoogleMedia } from '@/model/googlemedia.model';
import { ILibraryMusicFolder, libraryMusicService } from '@/services/library-music.service';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonNote,
  IonPage,
  IonProgressBar,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
  alertController,
} from '@ionic/vue';
import axios from 'axios';
import { preserveFileExtension } from '@/utils/filename-extension';
import { Preferences } from '@capacitor/preferences';
import { add, closeOutline, createOutline, musicalNotesOutline, trashOutline } from 'ionicons/icons';
import { defineComponent, inject } from 'vue';

interface AdminUserRow {
  id: number;
  login: string;
  firstName?: string;
  lastName?: string;
}

export default defineComponent({
  name: 'AdminLibraryMusic',
  components: {
    PageHeaderWithMenu,
    IonPage,
    IonContent,
    IonCard,
    IonCardHeader,
    IonCardTitle,
    IonCardContent,
    IonButton,
    IonIcon,
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonItem,
    IonInput,
    IonLabel,
    IonList,
    IonSelect,
    IonSelectOption,
    IonSearchbar,
    IonNote,
    IonProgressBar,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    return {
      globalContext,
      add,
      closeOutline,
      musicalNotesOutline,
      createOutline,
      trashOutline,
    };
  },
  data() {
    return {
      folders: [] as ILibraryMusicFolder[],
      headerScrolled: false,
      showFolderModal: false,
      showTracksModal: false,
      folderModalTitle: '',
      editingFolderId: null as number | null,
      editTitle: '',
      editAccess: 'PUBLIC' as 'PUBLIC' | 'RESTRICTED',
      selectedUserIds: new Set<number>(),
      allUsers: [] as AdminUserRow[],
      userSearch: '',
      saving: false,
      selectedFolder: null as ILibraryMusicFolder | null,
      tracks: [] as GoogleMedia[],
      uploadQueue: [] as Array<{ file: File; targetName: string }>,
      uploadFlushing: false,
      uploadProgressList: [] as Array<{ fileName: string; percent: number; status: string }>,
    };
  },
  computed: {
    isMobile(): boolean {
      return this.globalContext.isMobile;
    },
    filteredUsers(): AdminUserRow[] {
      const q = this.userSearch.trim().toLowerCase();
      if (!q) return this.allUsers.slice(0, 200);
      return this.allUsers
        .filter(
          (u) =>
            u.login.toLowerCase().includes(q) ||
            (u.firstName || '').toLowerCase().includes(q) ||
            (u.lastName || '').toLowerCase().includes(q)
        )
        .slice(0, 200);
    },
  },
  ionViewWillEnter() {
    this.refreshFolders();
    this.loadUsers();
  },
  methods: {
    onContentScroll(ev: CustomEvent) {
      const t = (ev.detail as { scrollTop?: number }).scrollTop || 0;
      this.headerScrolled = t > 8;
    },
    async refreshFolders() {
      try {
        this.folders = await libraryMusicService.adminListFolders();
      } catch (e) {
        console.error(e);
      }
    },
    async loadUsers() {
      try {
        const { data } = await axios.get<{ id: number; login: string; firstName?: string; lastName?: string }[]>(
          '/api/admin/users?page=0&size=500'
        );
        this.allUsers = data.map((u) => ({
          id: u.id,
          login: u.login,
          firstName: u.firstName,
          lastName: u.lastName,
        }));
      } catch (e) {
        console.error(e);
      }
    },
    openCreateModal() {
      this.editingFolderId = null;
      this.folderModalTitle = 'Новая папка';
      this.editTitle = '';
      this.editAccess = 'PUBLIC';
      this.selectedUserIds = new Set();
      this.showFolderModal = true;
    },
    openEditModal(f: ILibraryMusicFolder) {
      this.editingFolderId = f.id;
      this.folderModalTitle = 'Доступ: ' + f.title;
      this.editTitle = f.title;
      this.editAccess = f.accessMode;
      this.selectedUserIds = new Set(f.allowedUserIds || []);
      this.showFolderModal = true;
    },
    closeFolderModal() {
      this.showFolderModal = false;
    },
    toggleUserQuick(id: number) {
      if (this.selectedUserIds.has(id)) this.selectedUserIds.delete(id);
      else this.selectedUserIds.add(id);
      this.selectedUserIds = new Set(this.selectedUserIds);
    },
    async saveFolderModal() {
      if (!this.editTitle.trim()) return;
      this.saving = true;
      try {
        const allowedUserIds = this.editAccess === 'RESTRICTED' ? Array.from(this.selectedUserIds) : [];
        if (this.editingFolderId == null) {
          await libraryMusicService.adminCreateFolder({
            title: this.editTitle.trim(),
            accessMode: this.editAccess,
            allowedUserIds,
          });
        } else {
          await libraryMusicService.adminUpdateFolder(this.editingFolderId, {
            title: this.editTitle.trim(),
            accessMode: this.editAccess,
            allowedUserIds,
          });
        }
        await this.refreshFolders();
        this.closeFolderModal();
      } catch (e) {
        console.error(e);
      } finally {
        this.saving = false;
      }
    },
    async confirmDelete(f: ILibraryMusicFolder) {
      const a = await alertController.create({
        header: 'Удалить папку?',
        message: f.title,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: async () => {
              try {
                await libraryMusicService.adminDeleteFolder(f.id);
                await this.refreshFolders();
              } catch (e) {
                console.error(e);
              }
            },
          },
        ],
      });
      await a.present();
    },
    async selectFolder(f: ILibraryMusicFolder) {
      this.selectedFolder = f;
      this.showTracksModal = true;
      await this.loadTracks();
    },
    closeTracksModal() {
      this.showTracksModal = false;
      this.selectedFolder = null;
      this.tracks = [];
      this.uploadQueue = [];
      this.uploadProgressList = [];
    },
    async loadTracks() {
      if (!this.selectedFolder) return;
      try {
        this.tracks = await libraryMusicService.adminListTracks(this.selectedFolder.id);
      } catch (e) {
        console.error(e);
        this.tracks = [];
      }
    },
    pickAudio() {
      (this.$refs.fileInput as HTMLInputElement)?.click();
    },
    onFilesPicked(ev: Event) {
      const input = ev.target as HTMLInputElement;
      const files = input.files;
      if (!files?.length) return;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        this.uploadQueue.push({ file, targetName: file.name });
      }
      input.value = '';
    },
    onUploadTargetNameInput(q: { file: File; targetName: string }, ev: CustomEvent) {
      const raw = (ev.detail as { value?: string }).value ?? '';
      const fixed = preserveFileExtension(q.file.name, raw);
      if (fixed !== q.targetName) {
        q.targetName = fixed;
      }
    },
    async flushUpload() {
      if (!this.selectedFolder || !this.uploadQueue.length) return;
      this.uploadFlushing = true;
      this.uploadProgressList = [];
      const token = (await Preferences.get({ key: 'jwt' })).value;
      const queue = [...this.uploadQueue];
      try {
        for (let i = 0; i < queue.length; i++) {
          const q = queue[i];
          const displayName = preserveFileExtension(q.file.name, q.targetName?.trim() || q.file.name);
          this.uploadProgressList.push({
            fileName: displayName,
            percent: 0,
            status: 'Загрузка…',
          });
          const progressIndex = this.uploadProgressList.length - 1;
          const fd = new FormData();
          fd.append('file', q.file, q.file.name);
          if (q.targetName && q.targetName !== q.file.name) {
            fd.append('fileName', q.targetName);
          }
          await axios.post(`/api/admin/library-music/folders/${this.selectedFolder.id}/upload`, fd, {
            headers: { Authorization: `Bearer ${token}` },
            onUploadProgress: (progressEvent) => {
              const total = progressEvent.total;
              if (total && total > 0) {
                const pct = Math.round((progressEvent.loaded * 100) / total);
                const row = this.uploadProgressList[progressIndex];
                if (row) {
                  row.percent = pct;
                  row.status = pct >= 100 ? 'Готово' : 'Загрузка…';
                }
              }
            },
          });
          const done = this.uploadProgressList[progressIndex];
          if (done) {
            done.percent = 100;
            done.status = 'Готово';
          }
        }
        this.uploadQueue = [];
        await this.loadTracks();
      } catch (e) {
        console.error(e);
        const last = this.uploadProgressList[this.uploadProgressList.length - 1];
        if (last) last.status = 'Ошибка';
      } finally {
        this.uploadFlushing = false;
      }
    },
    async promptRename(t: GoogleMedia) {
      const a = await alertController.create({
        header: 'Переименовать',
        inputs: [{ name: 'to', type: 'text', value: t.name }],
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'OK',
            handler: async (d) => {
              const raw = (d as { to?: string }).to?.trim();
              if (!raw || !this.selectedFolder) return;
              const to = preserveFileExtension(t.id, raw);
              if (to === t.id) return;
              try {
                await axios.put(
                  `/api/admin/library-music/folders/${this.selectedFolder.id}/file/rename`,
                  null,
                  {
                    params: { from: t.id, to },
                  }
                );
                await this.loadTracks();
              } catch (e) {
                console.error(e);
              }
            },
          },
        ],
      });
      await a.present();
    },
    async confirmDeleteTrack(t: GoogleMedia) {
      const a = await alertController.create({
        header: 'Удалить файл?',
        message: t.name,
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Удалить',
            role: 'destructive',
            handler: async () => {
              if (!this.selectedFolder) return;
              try {
                await axios.delete(`/api/admin/library-music/folders/${this.selectedFolder.id}/file`, {
                  params: { filename: t.id },
                });
                await this.loadTracks();
              } catch (e) {
                console.error(e);
              }
            },
          },
        ],
      });
      await a.present();
    },
  },
});
</script>

<style scoped>
.admin-card {
  margin: 16px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  --color: rgba(255, 255, 255, 0.9);
}

.admin-card ion-card-title {
  color: white;
  font-weight: 600;
}

.slug-line {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 4px 0 0;
}

.admin-card ion-list {
  background: transparent;
}

.admin-card ion-item {
  --background: transparent;
  --background-activated: transparent;
  --background-hover: transparent;
  --background-focused: transparent;
  --color: rgba(255, 255, 255, 0.9);
  --border-color: rgba(255, 255, 255, 0.15);
}

.admin-card ion-item::part(native) {
  background: transparent;
}

.admin-card ion-item ion-label h2 {
  color: white;
  font-weight: 500;
}

.admin-card ion-item ion-label p {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9em;
}

.admin-card ion-item ion-icon {
  color: var(--ion-color-orange);
}

/* Кнопки в стиле приложения: оранжевые */
.admin-card ion-button:not([fill="clear"]):not([fill="outline"]) {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --color: white;
  --color-activated: white;
  font-weight: bold;
  --border-radius: 14px;
  margin-top: 8px;
}

.admin-card ion-button[fill="outline"] {
  --background: transparent;
  --border-width: 1px;
  --border-style: solid;
  --border-color: var(--ion-color-orange);
  --color: var(--ion-color-orange);
  --color-activated: var(--ion-color-orange-tint);
  font-weight: bold;
  --border-radius: 14px;
  margin-top: 8px;
}

.admin-card ion-button[fill="clear"] {
  --color: var(--ion-color-orange);
  --color-activated: var(--ion-color-orange-tint);
  font-weight: 600;
}

.admin-card ion-button[fill="clear"][color="danger"] {
  --color: var(--ion-color-danger);
  --color-activated: var(--ion-color-danger-tint);
}

.upload-progress-list {
  margin: 12px 0 16px;
}

.upload-progress-heading {
  margin: 0 0 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.upload-progress-item {
  margin-bottom: 14px;
}

.upload-progress-name {
  margin: 0 0 6px;
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.85);
  word-break: break-all;
}

.upload-progress-item ion-progress-bar {
  --progress-background: var(--ion-color-orange);
  --background: rgba(255, 255, 255, 0.12);
  height: 8px;
  border-radius: 4px;
}

.upload-progress-status {
  margin: 6px 0 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.55);
}
</style>
